import { currentUserHasPermission, getThreadTypeMeta, checkUserRights, isPremium, getMyAgenciesMappedIDs, safeParseJsonArray, getTaskTitleForUser, getTaskPriorityForUser, extractObserverUserIDs } from "../../Utils/Utilities"
import { useEffect, useState } from "react"
import DashboardHeader from "./Fragments/DashboardHeader"
import { useSelector } from "react-redux"
import CustomersCard from "./Fragments/CustomersCard"
import SuppliersCard from "./Fragments/SuppliersCard"
import BusinessActivity from "./Fragments/BusinessActivity"
import Leaderboard from "./Fragments/Leaderboard"
import TaskEntry from "../Tasks/TaskEntry"
import ConnectionEntryDialog from "../Connections/ConnectionEntry"
import { getActiveThreadTypesArr, getThreadTypesArr, userIsAdmin } from "../../Store/Reducers"
import Tasks from "./Fragments/Tasks"
import ThreadEntry from "../Threads/ThreadEntry"
import ConnectionDetails from "../Connections/ConnectionDetails"
import { useDesktop } from "../../Components/Common/IsDesktop"
import QuickStats from "./Fragments/QuickStats"
import Opportunities from "./Fragments/Opportunities"
import { Crown, Plus, TrendingUp } from "lucide-react"
import PurchasePipeline from "./Fragments/PurchasePipeline"
import ConnectionDetailsWrapper from "../Connections/ConnectionDetailsWrapper"
import BusinessActivityBar from "./Fragments/BusinessActivityBar"
import { AlertTriangle, Lightbulb } from "lucide-react"
import TktDialog from "../Ticket/TktDialog"
import { Button } from "../../Components/UI/button"
import { getUsersArr } from "../../Store/Reducers"
import { getAuthData } from '../../Utils/AuthUtils';
import CustomerContractsCard from "./Fragments/CustomerContractsCard"
import SupplierContractsCard from "./Fragments/SupplierContractCard"
import { customerContractsData, supplierContractsData } from "./DashboardData"
import ContractEntry from "../Contracts/ContractEntry"
import UpgradeSubscription from "../Settings/UpgradeSubscription/UpgradeSubscription"
import useIsMobile from "../../Hooks/useIsMobile"
import { getFromIndexedDB } from "../../Utils/IndexedDBUtils"
import PullToRefreshContainer from "../../Components/Common/PullToRefreshContainer"

const Dashboard = () => {
	// Local state variables
	const [isInitialized, setIsInitialized] = useState(false);
	const [isTaskEntryOpen, setIsTaskEntryOpen] = useState(false);
	const [taskEntryPrefillData, setTaskEntryPrefillData] = useState(null);
	const [allConnsFromDB, setAllConnsFromDB] = useState([]);
	const [upgradeOpen, setIsUpgradeOpen] = useState(false);
	const [isConnectionEntryOpen, setIsConnectionEntryOpen] = useState(false);
	const canAddConnections = currentUserHasPermission('ConnEntry');
	const isMobile = useIsMobile();

	const [isThreadEntryOpen, setIsThreadEntryOpen] = useState(false);
	const [dashboardHeaderData, setDashboardHeaderData] = useState({
		agencyID: null,
		agencyName: '',
		metrics: []
	});
	const [customersData, setCustomersData] = useState({
		active: 0,
		inactive: 0,
		future: 0
	});
	const [suppliersData, setSuppliersData] = useState({
		active: 0,
		inactive: 0,
		future: 0
	});
	const [currentConnType, setCurrentConnType] = useState('');
	const [businessActivities, setBusinessActivities] = useState([]);
	const [currentThreadTypeID, setCurrentThreadTypeID] = useState('');
	const [leaderboardData, setLeaderboardData] = useState([]);
	const [tasksData, setTasksData] = useState({
		todayTasks: [],
		overdueTasks: [],
		thisWeekTasks: [],
	});
	const [selectedConnection, setSelectedConnection] = useState(null);
	const [isDetailOpen, setIsDetailOpen] = useState(false);
	const [quickStatsTxn, setQuickStatsTxn] = useState([]);
	const [quickStatsOrders, setQuickStatsOrder] = useState([])
	const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
	const [selectedFeedbackType, setSelectedFeedbackType] = useState(null);
	const authData = getAuthData();
	const currentUserID = authData?.UserID;
	const [isContractEntryOpen, setIsContractEntryOpen] = useState(false);
	const [currentContractType, setCurrentContractType] = useState('');
	const [custCtrcData, setCustCtrcData] = useState({
		active: 0,
		expiring: 0,
		renewed: 0,
		new: 0
	})
	const [splrCtrcData, setSplrCtrcData] = useState({
		active: 0,
		expiring: 0,
		renewed: 0,
		new: 0
	})

	// Function which will handle close connection details dialog
	const handleDetailClose = () => setIsDetailOpen(false);

	// Handle feedback dialog
	const handleFeedbackOpen = (type) => {
		setSelectedFeedbackType(type);
		setIsFeedbackOpen(true);
	};

	const handleFeedbackClose = () => {
		setIsFeedbackOpen(false);
		setSelectedFeedbackType(null);
	};

	// Helper function to check if thread type should be shown based on Sh property
	const shouldShowThreadType = (tid) => {
		const threadType = threadTypes.find(t => t.TID === tid);
		return threadType ? (tType => tType.Sh != false) : false;
	};

	// Data which comes from Redux
	const { data: connectionData } = useSelector(state => state.connectionData);
	const { data: agencyData } = useSelector(state => state.agencyData);
	const { data: threadData } = useSelector(state => state.threadData);
	const userData = useSelector(getUsersArr);
	const { data: activeData } = useSelector(state => state.activeData);
	const threadTypes = useSelector(getActiveThreadTypesArr);
	const isAdmin = useSelector(userIsAdmin);
	const isDesktop = useDesktop();
	const loggedInUserInfo = userData.find(u => u.UserID === Number(currentUserID));
	const userRights = checkUserRights();
	const { data: taskData } = useSelector(state => state.taskData);
	const isOrderVisible = shouldShowThreadType(2);
	const isInquiryVisible = shouldShowThreadType(1);

	useEffect(() => {
		const timer = setTimeout(() => {
			setIsInitialized(true);
		}, 10);
		return () => clearTimeout(timer);
	}, []);

	// Update task data when connection data is changed
	useEffect(() => {
		const loadAllConns = async () => {
			try {
				const conns = await getFromIndexedDB('Conns') || [];
				setAllConnsFromDB(conns);
			} catch (e) {
				console.error("Failed to load connections from DB in Dashboard", e);
			}
		};
		loadAllConns();
	}, [connectionData]);

	useEffect(() => {
		setConnectionsData();
		setTasksData(getProcessedTasks(taskData, connectionData));
	}, [taskData, connectionData, allConnsFromDB]);

	// Update bussiness activity data when thread data is changed
	// (also recompute when the active agency/user/activeData changes, e.g. on an agency switch,
	// since getLeaderboardData/getDashboardHeaderData read those from closure)
	useEffect(() => {
		setLeaderboardData(getLeaderboardData());
		setDashboardHeaderData(getDashboardHeaderData());
		setBusinessActivities(transformThreadsToActivities(threadData, threadTypes));
	}, [threadData, threadTypes, agencyData, activeData, userData]);

	// Calculate dashboard header data
	const calculateMetrics = (data) => {
		if (!Array.isArray(data) || data.length === 0) return null;

		// Split into inquiries and orders using a single filter
		const inquiries = [];
		const orders = [];
		data.forEach(d => {
			if (d.TID === 1) inquiries.push(d);
			else if (d.TID === 2) orders.push(d);
		});

		const metrics = [];

		// Find current month from orders (latest by YY/MM)
		const sortedOrders = [...orders].sort((a, b) => (a.YY - b.YY) || (a.MM - b.MM));
		const latestOrder = sortedOrders[sortedOrders.length - 1];

		if (latestOrder) {
			const { YY, MM } = latestOrder;

			// Find matching inquiry for the same month, fallback to 0
			const sameMonthInquiry = inquiries.find(i => i.YY === YY && i.MM === MM) || { Rcvd: 0, Pndg: 0 };

			const rcvdInq = sameMonthInquiry.Rcvd || 0; // use (Rcvd + Pndg) if you want pending included
			const conversion = rcvdInq > 0 ? (latestOrder.Rcvd / rcvdInq) * 100 : 0;
			isInquiryVisible && metrics.push({
				label: "Conversion",
				labelM: "Conversion",
				value: Math.round(conversion) + '%',
				color: "var(--sahi-green)",
			});

			if (sortedOrders.length >= 2 && isOrderVisible) {
				const prevOrder = sortedOrders[sortedOrders.length - 2];
				const prevOrderRcvd = prevOrder.Rcvd || 0;
				const orderGrowth = prevOrderRcvd > 0 ? ((latestOrder.Rcvd - prevOrderRcvd) / prevOrderRcvd) * 100 : 0;
				isOrderVisible && metrics.push({
					label: "Order Growth",
					labelM: "Growth",
					value: (orderGrowth >= 0 ? '+' : '') + Math.round(orderGrowth) + '%',
					color: "var(--sahi-blue)",
				});
			}

			// 3) Punctuality (this month according to late order)
			let cntDoneOrds = latestOrder.Rcvd - (latestOrder.Pndg || 0);
			if (cntDoneOrds < 0) cntDoneOrds = 0; // just in case
			// Punctuality = (Done - Late) / Done * 100
			const punctuality = cntDoneOrds ? ((cntDoneOrds - (latestOrder.Late || 0)) / cntDoneOrds) * 100 : 0;
			isOrderVisible && metrics.push({
				label: "Punctuality",
				labelM: "Punctuality",
				value: Math.round(punctuality) + '%',
				color: "var(--sahi-orange)",
			});
		}

		// 4) Average Order (from threadData where TypeID=2 and StatusID=0)
		const openOrders = threadData.filter(t => Number(t.TypeID) === 2 && Number(t.StatusID) === 0);
		const avgOrder = openOrders.length
			? openOrders.reduce((sum, t) => sum + (t.BizProspect || 0), 0) / openOrders.length
			: 0;
		isOrderVisible && metrics.push({
			label: "Avg Order",
			labelM: "Avg Order",
			value: Math.round(avgOrder).toLocaleString(),
			color: "var(--text-color)",
		});

		return metrics;
	};

	useEffect(() => {
		if (!activeData) return;

		if (activeData?.MonthlyXns) {
			const transactionDetails = JSON.parse(activeData.MonthlyXns || "[]");
			const stats = calculateQuickStats(transactionDetails, "transactions");
			setQuickStatsTxn(stats);
		}
		if (activeData?.MonthlyThreads) {
			const orderData = JSON.parse(activeData.MonthlyThreads || "[]");
			const orders = calculateQuickStats(orderData, "orders");
			setQuickStatsOrder(orders);
		}
	}, [activeData?.MonthlyXns, activeData?.MonthlyThreads]);

	const calculateQuickStats = (transactionDetails, type = "transactions") => {
		const now = new Date();
		const currentYY = now.getFullYear();
		const currentMM = now.getMonth() + 1; // JS months are 0-based
		const previousYY = currentMM === 1 ? currentYY - 1 : currentYY;
		const previousMM = currentMM === 1 ? 12 : currentMM - 1;

		// Group by (YY,MM,TID)
		const groupedData = transactionDetails.reduce((acc, t) => {
			const yy = Number(t.YY);
			const mm = Number(t.MM);
			const tid = Number(t.TID);
			const amt = Number(t.Amt);

			if (!yy || !mm || !tid) return acc;

			const key = `${yy}-${mm.toString().padStart(2, "0")}-${tid}`;
			acc[key] = (acc[key] || 0) + Math.abs(amt);
			return acc;
		}, {});

		// Helper
		const getAmount = (yy, mm, tid) => {
			const key = `${yy}-${mm.toString().padStart(2, "0")}-${tid}`;
			return groupedData[key] || 0;
		};

		const calculateStats = (tid) => {
			const currentAmt = getAmount(currentYY, currentMM, tid);
			const previousAmt = getAmount(previousYY, previousMM, tid);

			let percentageChange = 0;

			if (previousAmt > 0) {
				percentageChange = Number(
					(((currentAmt - previousAmt) / previousAmt) * 100).toFixed(0)
				);
			}

			return { amount: currentAmt, percentageChange };
		};

		if (type === "transactions") {
			return {
				currentYear: currentYY,
				currentMonth: currentMM,
				previousYear: previousYY,
				previousMonth: previousMM,
				salesInvoice: calculateStats(1),
				purchaseInvoice: calculateStats(2),
				receipt: calculateStats(5),
				payment: calculateStats(6)
			};
		} else {
			return {
				currentYear: currentYY,
				currentMonth: currentMM,
				previousYear: previousYY,
				previousMonth: previousMM,
				salesOrder: calculateStats(2),
				purchaseOrder: calculateStats(5)
			};
		}
	};

	// Usage inside getDashboardHeaderData
	const getDashboardHeaderData = () => {
		const monthlySOs = activeData?.MonthlyThreads;
		const logoPath = agencyData?.Logo;

		const monthlyData = monthlySOs ? JSON.parse(monthlySOs) : [];
		const metrics = monthlyData.length ? calculateMetrics(monthlyData, threadData) : [];

		const agencyName = agencyData?.AgencyName || "N/A";
		const agencyID = agencyData?.AgencyID || null;

		return {
			agencyID,
			agencyName,
			logoPath,
			metrics: metrics || [],
		};
	};

	// Function which calculate customers and suppliers count
	const setConnectionsData = () => {
		const customerThreadTIDs = [2, 3]; // Sales Order, Support Ticket
		const supplierThreadTIDs = [5, 6]; // Purchase Order, Supplier Complaint

		const customerTxnTIDs = [1, 3, 5]; // Sales Invoice, Credit Note, Receipt
		const supplierTxnTIDs = [2, 4, 6]; // Purchase Invoice, Debit Note, Payment

		let custCounts = { active: 0, inactive: 0, future: 0 };
		let suppCounts = { active: 0, inactive: 0, future: 0 };

		connectionData.forEach((conn) => {
			let cntTypewiseThreads = [];
			let cntTypewiseXns = [];

			if (conn.CntTypewiseThreads) {
				try {
					cntTypewiseThreads = JSON.parse(conn.CntTypewiseThreads);
				} catch {
					cntTypewiseThreads = [];
				}
			}

			if (conn.CntTypewiseXns) {
				try {
					cntTypewiseXns = JSON.parse(conn.CntTypewiseXns);
				} catch {
					cntTypewiseXns = [];
				}
			}

			const customerThreads = cntTypewiseThreads.filter((t) =>
				customerThreadTIDs.includes(t.TID)
			);
			const supplierThreads = cntTypewiseThreads.filter((t) =>
				supplierThreadTIDs.includes(t.TID)
			);

			const isCustomer = conn.ConnType?.includes("C");
			const isSupplier = conn.ConnType?.includes("S");

			// --- THREADS CALC ---
			const custOpenSum = customerThreads.reduce((sum, t) => sum + (t.CntOpen || 0), 0);
			const custAllSum = customerThreads.reduce((sum, t) => sum + (t.CntAll || 0), 0);

			const suppOpenSum = supplierThreads.reduce((sum, t) => sum + (t.CntOpen || 0), 0);
			const suppAllSum = supplierThreads.reduce((sum, t) => sum + (t.CntAll || 0), 0);

			// --- TXNS CALC ---
			const customerHasTxn = cntTypewiseXns.some(
				(t) => customerTxnTIDs.includes(t.TID) && (t.CntAll || 0) > 0
			);

			const supplierHasTxn = cntTypewiseXns.some(
				(t) => supplierTxnTIDs.includes(t.TID) && (t.CntAll || 0) > 0
			);

			// --- CONTRACT CALC ---
			const hasContracts = conn.CtrcIDs && conn.CtrcIDs.trim() !== '';

			// --- FINAL COUNTS ---
			if (isCustomer) {
				if (custOpenSum > 0 || customerHasTxn || hasContracts) {
					custCounts.active++;
				} else if (custAllSum > 0 && !customerHasTxn) {
					custCounts.inactive++;
				} else {
					custCounts.future++;
				}
			}

			if (isSupplier) {
				if (suppOpenSum > 0 || supplierHasTxn || hasContracts) {
					suppCounts.active++;
				} else if (suppAllSum > 0 && !supplierHasTxn) {
					suppCounts.inactive++;
				} else {
					suppCounts.future++;
				}
			}
		});

		setCustomersData(custCounts);
		setSuppliersData(suppCounts);
	};

	// Function which will return data for bussiness activity table
	const transformThreadsToActivities = (threads, threadTypes) => {
		const today = new Date();

		return threadTypes.map((type) => {
			if (!(userRights?.canSeeCstrs && type?.Flow === "In" || userRights?.canSeeSplrs && type?.Flow === "Out")) {
				return null
			}
			const matchingThreads = threads.filter(t => t.TypeID === type.TID);

			const openThreads = matchingThreads.filter(t => t.StatusID === 0);
			const open = openThreads.length;

			const delayed = openThreads.filter(t => {
				return t.DueDT && new Date(t.DueDT) < today;
			}).length;

			const totalBizProspect = openThreads.reduce((sum, t) => sum + t.BizProspect, 0);

			const meta = getThreadTypeMeta(type.TID, threadTypes);

			return {
				id: type.TID,
				name: meta.name,
				icon: meta.Icon,
				iconType: meta.isFontAwesome ? "fontawesome" : "react",
				color: meta.textColor,
				open,
				delayed,
				value: totalBizProspect.toLocaleString(),
			};
		});
	};

	// Function which will return data for leaderboard
	const getLeaderboardData = () => {
		if (!userData || userData.length === 0) return [];

		const monthlySOs = activeData?.MonthlyThreads;

		// Parse Usrs data if available
		let usrs = [];
		if (monthlySOs) {
			try {
				const monthlyData = JSON.parse(monthlySOs);
				const salesOrders = monthlyData
					.filter(m => m.TID === 2)
					.sort((a, b) => (a.YY - b.YY) || (a.MM - b.MM));

				if (salesOrders.length > 0) {
					const lastOrder = salesOrders[salesOrders.length - 1];
					usrs = safeParseJsonArray(lastOrder.Usrs);
				}
			} catch {
				usrs = [];
			}
		}

		// Create leaderboard with all users
		const leaderboard = userData.map(user => {
			// Find user's performance data in usrs array
			const userPerformance = usrs.find(u => u.UID === user.UserID);

			return {
				id: user.UserID,
				name: user.Name || "-",
				price: userPerformance?.RA || 0,
				orders: userPerformance?.RC || 0,
			};
		});

		const sortedUsers = leaderboard.sort((a, b) => {
			if (b.price !== a.price) {
				return b.price - a.price
			}
			if (a.price !== 0 && b.price !== 0) {
				if (b.orders !== a.orders) {
					return b.orders - a.orders;
				}
			}
			return a.name.localeCompare(b.name);
		});

		const top5Users = sortedUsers.slice(0, 5);
		return top5Users;

		// Filter users with orders and sort by price descending, then by orders descending
		const usersWithOrders = leaderboard
			.filter(u => u.price > 0 && u.orders > 0)
			.sort((a, b) => {
				if (b.price !== a.price) return b.price - a.price;
				return b.orders - a.orders;
			});

		// If no users have orders, show current user with 0 orders and 0 amount
		if (usersWithOrders.length === 0 && loggedInUserInfo) {
			return [{
				id: loggedInUserInfo.UserID,
				name: loggedInUserInfo.Name || "-",
				price: 0,
				orders: 0,
			}];
		}

		return usersWithOrders;
	};

	// Function which give prossesed task data
	const getProcessedTasks = (taskData, connectionData) => {
		if (!Array.isArray(taskData)) return { todayTasks: [], overdueTasks: [], thisWeekTasks: [] };

		// Filter tasks with Status 1 only, assigned to/created by current user or mapped agency users, or where user is an observer on meeting tasks
		const filteredTasks = taskData.filter((task) => {
			if (task.Status !== 1) return false;
			const myUserIDs = getMyAgenciesMappedIDs('UserID');
			const userIDsToCheck = myUserIDs.length > 0 ? myUserIDs : [Number(currentUserID)];
			const isAssignedToMe = userIDsToCheck.includes(Number(task.POCUserID));
			const isAssignedByMe = userIDsToCheck.includes(Number(task.CreatedUserID));
			const classId = Number(task.Class || task.class || task.taskClass || task.TaskClass || 1);
			const obsIDs = extractObserverUserIDs(task);
			const isObserver = obsIDs.some(id => userIDsToCheck.includes(Number(id)));
			return isAssignedToMe || isAssignedByMe || (classId === 2 && isObserver);
		});

		const today = new Date();
		today.setHours(0, 0, 0, 0);

		const endOfToday = new Date(today);
		endOfToday.setHours(23, 59, 59, 999);

		const endOfNext7Days = new Date(today);
		endOfNext7Days.setDate(endOfNext7Days.getDate() + 7);
		endOfNext7Days.setHours(23, 59, 59, 999);

		const todayTasks = [];
		const overdueTasks = [];
		const thisWeekTasks = [];

		filteredTasks.forEach(task => {
			// Find connection data for this task
			let conn = connectionData.find(c => c.ConnID === task.ConnID);
			if (!conn && allConnsFromDB.length > 0) {
				conn = allConnsFromDB.find(c => Number(c.ConnID || c.connID || 0) === Number(task.ConnID));
			}

			// Skip tasks from cold contacts
			if (conn && (Number(conn.IsCold) === 1 || conn.IsCold === true)) {
				return;
			}

			const taskDate = new Date(task.DueDT);
			const taskDateStart = new Date(taskDate);
			taskDateStart.setHours(0, 0, 0, 0);

			const taskObj = {
				TskID: task.TaskID,
				Ttl: getTaskTitleForUser(task, currentUserID),
				BizName: conn?.BizName || '',
				ContactName: conn?.ContactName || '',
				DT: task.DueDT,
				Pri: getTaskPriorityForUser(task, currentUserID),
				ConnID: task.ConnID,
				UID: task.POCUserID,
				Typ: task.TaskType || "",
				Txt: task.Description,
				Sts: task.Status,
				TIDs: task.ThreadIDs,
				CreatedUID: task.CreatedUserID,
				Mobile: task.CreatorMobile || task.creatorMobile || task.Mobile || task.mobile || task.Mob || task.mob || null,
				CreatedMobile: task.CreatorMobile || task.creatorMobile || task.CreatedMobile || task.Mobile || task.mobile || task.Mob || task.mob || null,
				POCMobile: task.POCMobile || task.pocMobile || task.Mobile || task.mobile || task.Mob || task.mob || null,
				CreatedCtgID: task.CreatedCtgID,
				POCCtgID: task.POCCtgID,
				AgencyID: task.AgencyID || conn?.AgencyID,
				CanComplete: task.CanComplete,
				RepeatAfter: task.RepeatAfter,
				RepeatDT: task.RepeatDT,
				Class: task.Class || task.class || task.TaskClass || task.taskClass || 1,
			};

			if (taskDateStart.getTime() === today.getTime()) {
				todayTasks.push(taskObj);
			} else if (taskDateStart < today) {
				overdueTasks.push(taskObj);
			} else if (taskDate > endOfToday && taskDate <= endOfNext7Days) {
				thisWeekTasks.push(taskObj);
			}
		});

		return {
			todayTasks,
			overdueTasks,
			thisWeekTasks
		};
	};

	// Handle connection entry dialog
	const handleConnectionEntryOpen = (connType) => {
		setCurrentConnType(connType);
		setIsConnectionEntryOpen(true);
	}

	// Handle contract entry dialog
	const handleContractEntryOpen = (ctrcType) => {
		setCurrentContractType(ctrcType);
		setIsContractEntryOpen(true);
	}

	// Handle task entry dialog
	const handleTaskEntryOpen = () => {
		setTaskEntryPrefillData(null);
		setIsTaskEntryOpen(true);
	}

	const handleRepeatTaskEntryOpen = (prefillData) => {
		setTaskEntryPrefillData(prefillData);
		setIsTaskEntryOpen(true);
	}

	// Handle thread entery dialog
	const handleThreadEntryOpen = (threadTypeID) => {
		setCurrentThreadTypeID(threadTypeID);
		setIsThreadEntryOpen(true);
	}

	/*try {
	window.location.href = "enablepulltorefresh://";
  } catch (e) {
	return e;
  }*/


	// Mobile version with custom component order
	if (isMobile) {
		return (
			<div className="flex flex-col h-screen">
				{/* Main Content Area - Mobile */}
				<PullToRefreshContainer
					className="flex-1 overflow-y-auto overflow-x-hidden pt-20 pb-24 bg-bg-grey"
					onRefresh={() => window.location.reload()}
				>
					<div className="space-y-6">
						{/* Dashboard Header - Mobile */}
						<DashboardHeader isMobile={true} dashboardHeaderData={dashboardHeaderData} isAdmin={isAdmin} />

						{/* Opportunities - Mobile (First Priority) */}
						<div className="px-4">
							<Opportunities
								businessActivities={businessActivities}
								onThreadEntryOpen={handleThreadEntryOpen}
								threadData={threadData}
								isMobile={true}
							/>
						</div>

						<div className={`px-4 ${checkUserRights().canSeeCstrs == true ? '' : '!mt-0'}`}>
							<QuickStats
								quickStatsTxn={quickStatsTxn}
								quickStatsOrders={quickStatsOrders}
								showOrders={shouldShowThreadType(2)}
								showPOs={shouldShowThreadType(5)}
							/>
						</div>

						{/* PurchasePipeline - Mobile (First Priority) */}
						<div className={`px-4 ${checkUserRights().canSeeSplrs == true ? '' : '!mt-0'}`}>
							<PurchasePipeline
								businessActivities={businessActivities}
								onThreadEntryOpen={handleThreadEntryOpen}
								threadData={threadData}
								isMobile={true}
							/>
						</div>

						{/* Network Section Title - Mobile */}
						<div className="px-4">
							<div className="flex flex-col bg-sahi-white rounded-xl pt-4 pb-1">
								<div className="flex justify-between px-4 mb-4">
									<div className="text-text-color text-xl font-semibold">
										Business Network
									</div>

									{canAddConnections && (
										<div
											className="p-1 text-sahi-primary-color hover:cursor-pointer hover:bg-sahi-blue-light sahi-radius duration-200"
											onClick={() => handleConnectionEntryOpen("Customer")}
										>
											<Plus className="h-6 w-6" />
										</div>
									)}
								</div>
								{(userRights?.canSeeCstrs) && (
									<CustomersCard
										customerData={customersData}
										onConnectionEntryOpen={handleConnectionEntryOpen}
									/>
								)}
								{(userRights?.canSeeSplrs) && (
									<SuppliersCard
										supplierData={suppliersData}
										onConnectionEntryOpen={handleConnectionEntryOpen}
									/>
								)}
							</div>
						</div>

						{/* Leaderboard - Mobile */}
						<div className="px-4">
							<Leaderboard leaderboardData={leaderboardData} isAdmin={isAdmin} isOrderVisible={true} />
						</div>

						{/* Contract - Mobile */}
						{/* <div className="px-4">
              <div className="flex flex-col bg-sahi-white rounded-xl pt-4 pb-1">
                <div className="flex justify-between px-4 mb-4">
                  <div className="text-text-color text-2xl font-semibold">
                    Contracts
                  </div>
                    <div
                      className="p-1 text-sahi-primary-color hover:cursor-pointer hover:bg-sahi-blue-light sahi-radius duration-200"
                      onClick={() => handleConnectionEntryOpen("Customer")}
                    >
                      <Plus className="h-6 w-6" />
                    </div>
                </div>
                <CustomerContractsCard
                  contractData={customerContractsData}
                />
                <SupplierContractsCard
                  contractData={supplierContractsData}
                />
              </div>
            </div> */}

						{/* Tasks Section - Mobile */}
						<div className="px-4">
							<Tasks processedData={tasksData} isMobile={true} onTaskEntryOpen={handleTaskEntryOpen} onRepeatTaskEntryOpen={handleRepeatTaskEntryOpen} />
						</div>
					</div>
				</PullToRefreshContainer>

				{/* Dialogs - Mobile */}
				<TaskEntry
					triggerButton={false}
					open={isTaskEntryOpen}
					onOpenChange={(open) => { setIsTaskEntryOpen(open); if (!open) setTaskEntryPrefillData(null); }}
					prefilledData={taskEntryPrefillData}
				/>

				<ConnectionEntryDialog
					triggerButton={false}
					open={isConnectionEntryOpen}
					onOpenChange={setIsConnectionEntryOpen}
					initialConnType={currentConnType}
					onConnectionAdded={(savedConnection) => {
						if (savedConnection) {
							setSelectedConnection(savedConnection)
							setIsDetailOpen(true)
						}
					}}
				/>

				{selectedConnection && (
					<ConnectionDetailsWrapper
						isOpen={isDetailOpen}
						onClose={handleDetailClose}
						connID={selectedConnection.connID}
					/>
				)}

				<ThreadEntry
					triggerButton={false}
					open={isThreadEntryOpen}
					onOpenChange={setIsThreadEntryOpen}
					threadTypeID={currentThreadTypeID}
				/>
			</div>
		)
	}

	// Desktop version (original layout)
	return (
		<div className="flex flex-col lg:flex-row h-screen">
			{/* Main Content Area - Desktop */}
			<div className="flex-1 overflow-y-auto overflow-x-hidden md:p-4 4xl:p-6 py-20 pt-14 md:bg-bg-grey bg-sahi-white">

				<div className="4xl:space-y-6 space-y-4">
					{/* Customers and Suppliers Row - Desktop */}
					<div className="grid grid-cols-1 md:grid-cols-2 4xl:gap-6 md:gap-4 gap-3">
						{/* Dashboard Header - Desktop */}
						<DashboardHeader dashboardHeaderData={dashboardHeaderData} isAdmin={isAdmin} />

						{/* Customers - Desktop */}
						{(userRights?.canSeeCstrs) && (
							<CustomersCard
								customerData={customersData}
								onConnectionEntryOpen={handleConnectionEntryOpen}
							/>
						)}
						{/* Suppliers - Desktop */}
						{(userRights?.canSeeSplrs) && (
							<SuppliersCard
								supplierData={suppliersData}
								onConnectionEntryOpen={handleConnectionEntryOpen}
							/>
						)}
					</div>

					{/* Business Activity Table and Leaderboard Row - Desktop */}
					<div className="grid grid-cols-1 xl:grid-cols-3 4xl:gap-6 gap-4 items-start">
						{/* Business Activity - Desktop (spans 2 columns) */}
						<div className="xl:col-span-2 flex item-center flex-col 4xl:gap-6 gap-4">
							<BusinessActivityBar
								businessActivities={businessActivities}
								onThreadEntryOpen={handleThreadEntryOpen}
								threadData={threadData}
							/>
						</div>
						<div>
							{/* Enhanced Leaderboard - Desktop */}
							<Leaderboard leaderboardData={leaderboardData} isAdmin={isAdmin} isOrderVisible={true} />
							{/* Simple Feedback Buttons - Desktop Only */}
							{isDesktop && (
								<div className="flex 4xl:gap-3 gap-2 mt-6 flex-wrap">
									<Button
										onClick={() => handleFeedbackOpen(1)}
										variant="secondary"
										className="!border-sahi-red !text-sahi-red hover:bg-sahi-red hover:!text-white"
									>
										<AlertTriangle className="h-4 w-4" />
										Report Bug
									</Button>
									<Button
										onClick={() => handleFeedbackOpen(2)}
										variant="secondary"
										className="hover:bg-sahi-green text-sahi-green border-sahi-green"
									>
										<Lightbulb className="h-4 w-4" />
										Share Idea
									</Button>
									{!isPremium() && <Button
										onClick={() => setIsUpgradeOpen(true)}
										variant="secondary"
									>
										<Crown className="h-4 w-4" />
										Upgrade
									</Button>}
								</div>
							)}
						</div>
					</div>

					{/* <div className="grid grid-cols-1 md:grid-cols-2 md:gap-6 gap-3">
						<CustomerContractsCard 
						contractData={customerContractsData}
						onContractEntryOpen={handleContractEntryOpen}
					/>

					<SupplierContractsCard 
					contractData={supplierContractsData}
					onContractEntryOpen={handleContractEntryOpen}
					/>
         			 </div> */}

				</div>
			</div>

			{/* Enhanced Right Sidebar - Tasks (Desktop Only) */}
			<Tasks processedData={tasksData} onTaskEntryOpen={handleTaskEntryOpen} onRepeatTaskEntryOpen={handleRepeatTaskEntryOpen} />

			{/* Dialogs - Desktop */}
			<TaskEntry
				triggerButton={false}
				open={isTaskEntryOpen}
				onOpenChange={(open) => { setIsTaskEntryOpen(open); if (!open) setTaskEntryPrefillData(null); }}
				prefilledData={taskEntryPrefillData}
			/>

			<ConnectionEntryDialog
				triggerButton={false}
				open={isConnectionEntryOpen}
				onOpenChange={setIsConnectionEntryOpen}
				initialConnType={currentConnType}
				onConnectionAdded={(savedConnection) => {
					if (savedConnection) {
						setSelectedConnection(savedConnection)
						setIsDetailOpen(true)
					}
				}}
			/>

			{selectedConnection && (
				<ConnectionDetailsWrapper
					isOpen={isDetailOpen}
					onClose={handleDetailClose}
					connID={selectedConnection.connID}
				/>
			)}

			<ThreadEntry
				triggerButton={false}
				open={isThreadEntryOpen}
				onOpenChange={setIsThreadEntryOpen}
				threadTypeID={currentThreadTypeID}
			/>

			{/* Feedback Dialog */}
			<TktDialog
				isOpen={isFeedbackOpen}
				onClose={handleFeedbackClose}
				selectedFeedbackType={selectedFeedbackType}
			/>

			{/* Contract dialog */}
			<ContractEntry
				isOpen={isContractEntryOpen}
				onClose={setIsContractEntryOpen}
				prefilledData={{ contractTypeSelection: currentContractType }}
			/>
			{/* Contract dialog */}
			<UpgradeSubscription
				open={upgradeOpen}
				onClose={setIsUpgradeOpen}
			/>
		</div>
	)
}

export default Dashboard;
